import { useState } from 'react';
import { useDebounce } from '@/hooks/useDebounce';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Plus, Search } from 'lucide-react';
import { useBancos } from '@/features/financeiro/hooks/useCadastrosFinanceiros';
import { BancosTable } from '@/features/financeiro/components/BancosTable';
import { BancoDialog } from '@/features/financeiro/components/BancoDialog';
import type { Banco, SalvarBancoRequest } from '@/features/financeiro/types';
import { usePaginacaoServidor } from '@/hooks/usePaginacaoServidor';
import { PaginacaoRodape } from '@/components/PaginacaoRodape';

const Bancos = () => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingBanco, setEditingBanco] = useState<Banco | null>(null);
  const [busca, setBusca] = useState('');
  const buscaDebounced = useDebounce(busca, 400);

  const pag = usePaginacaoServidor({ resetarQuando: [buscaDebounced] });
  const { response, isLoading, criar, atualizar, excluir, isCriando, isAtualizando } = useBancos(buscaDebounced, pag.pagina, pag.tamanhoPagina);
  const bancos = response?.itens ?? [];

  const handleOpenDialog = (banco?: Banco) => {
    setEditingBanco(banco ?? null);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingBanco(null);
  };

  const handleSubmit = (data: SalvarBancoRequest) => {
    if (editingBanco) {
      atualizar({ id: editingBanco.id, req: data }, { onSuccess: handleCloseDialog });
    } else {
      criar(data, { onSuccess: handleCloseDialog });
    }
  };

  if (isLoading) {
    return <div className="flex items-center justify-center h-96"><p className="text-muted-foreground">Carregando...</p></div>;
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold">Bancos</h1>
          <p className="text-muted-foreground">Gerencie os bancos cadastrados</p>
        </div>
        <Button onClick={() => handleOpenDialog()}>
          <Plus className="h-4 w-4 mr-2" />Novo Banco
        </Button>
      </div>

      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Buscar banco..."
          value={busca}
          onChange={(e) => setBusca(e.target.value)}
          className="pl-10"
        />
      </div>

      <BancosTable
        bancos={bancos}
        onEdit={handleOpenDialog}
        onDelete={(id) => excluir(id)}
      />

      <PaginacaoRodape {...pag.rodape(response)} />

      <BancoDialog
        open={dialogOpen}
        onOpenChange={handleCloseDialog}
        onSubmit={handleSubmit}
        editingBanco={editingBanco}
        isLoading={isCriando || isAtualizando}
      />
    </div>
  );
};

export default Bancos;
